// REACT
import { useState } from "react";

// REDUX
import { useBeneficiarySlice } from "src/store/features/beneficiary.slice";

export const useValidateForm = () => {
  // Local State
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Beneficiary Slice
  const {
    currency,
    beneficiaryName,
    beneficiaryAddress,
    bankName,
    bankAddress,
    swiftCode,
    sortCodeOrBsb,
    ibanAccNumber,
    transitNumber,
    institutionNumber,
    abaRoutingNumber,
    isBeneficiaryAddressEntered,
    isBankAddressEntered,
    beneficiaryAddressSearch,
    bankAddressSearch,
  } = useBeneficiarySlice();

  const validateFields = () => {
    const newErrors: { [key: string]: string } = {};

    const isAudOrGbp = currency.value === "AUD" || currency.value === "GBP";

    // Currency validation //
    if (!currency.value) newErrors.currency = "This is a required field";

    // Beneficiary address validation //
    if (!beneficiaryName) newErrors.beneficiaryName = "This is a required field";

    if (!isBeneficiaryAddressEntered) {
      newErrors.beneficiaryAddressSearch = "This is a required field";
    }

    if (!isBeneficiaryAddressEntered && beneficiaryAddressSearch) {
      newErrors.beneficiaryAddressSearch = "Please select an address from the dropdown";
    }

    if (isBeneficiaryAddressEntered) {
      if (!beneficiaryAddress?.streetAddress)
        newErrors.beneficiaryStreetAddress = "This is a required field";
      if (!beneficiaryAddress?.city) newErrors.beneficiaryCity = "This is a required field";
      if (!beneficiaryAddress?.country) newErrors.beneficiaryCountry = "This is a required field";
      if (!beneficiaryAddress?.state) newErrors.beneficiaryState = "This is a required field";
      if (!beneficiaryAddress?.postcode) newErrors.beneficiaryPostcode = "This is a required field";
    }

    // Bank address validation //
    if (!bankName) newErrors.bankName = "This is a required field";

    if (!isBankAddressEntered) {
      newErrors.bankAddressSearch = "This is a required field";
    }

    if (!isBankAddressEntered && bankAddressSearch) {
      newErrors.bankAddressSearch = "Please select an address from the dropdown";
    }

    if (isBankAddressEntered) {
      if (!bankAddress?.streetAddress) newErrors.bankStreet = "This is a required field";
      if (!bankAddress?.city) newErrors.bankCity = "This is a required field";
      if (!bankAddress?.country) newErrors.bankCountry = "This is a required field";
      if (!bankAddress?.state) newErrors.bankState = "This is a required field";
      if (!bankAddress?.postcode) newErrors.bankPostcode = "This is a required field";
    }

    // Currency/Field validation //
    if (currency.value !== "USD" && abaRoutingNumber) {
      newErrors.abaRoutingNumber = "This field is only required for USD currency";
    }
    if (currency.value !== "CAD" && institutionNumber)
      newErrors.institutionNumber = "This field is only required for CAD currency";
    if (currency.value !== "CAD" && transitNumber)
      newErrors.transitNumber = "This field is only required for CAD currency";

    // Code/Number length validation //
    // IBAN/Account Number
    if (!ibanAccNumber) {
      newErrors.ibanAccNumber = "This is a required field";
    } else if (ibanAccNumber && ibanAccNumber.length < 6) {
      newErrors.ibanAccNumber = `Please ensure your ${
        currency.value === "GBP" || currency.value === "EUR" ? "IBAN" : "Account Number"
      } has at least 6 digits`;
    }

    // ABA Routing Number
    if ((currency.value === "USD" && !swiftCode) || abaRoutingNumber) {
      if (abaRoutingNumber?.length !== 9)
        newErrors.abaRoutingNumber = "Please ensure your ABA Routing Number has 9 digits";
      if (!abaRoutingNumber)
        newErrors.abaRoutingNumber = "Either ABA Routing Number or SWIFT Code is required";
    }

    // Sort Code/BSB
    if (isAudOrGbp && !sortCodeOrBsb) {
      newErrors.sortCodeOrBsb = "This is a required field";
    } else if (isAudOrGbp && sortCodeOrBsb?.length !== 6) {
      newErrors.sortCodeOrBsb = `Please ensure your ${
        currency.value === "AUD" ? "BSB" : "Sort Code"
      } has 6 digits`;
    }

    // Transit Number
    if (currency.value === "CAD" && institutionNumber?.length !== 3)
      newErrors.institutionNumber = "Please ensure your Institution Number has 3 digits";
    if (currency.value === "CAD" && !institutionNumber)
      newErrors.institutionNumber = "This is a required field";

    // Institution Number
    if (currency.value === "CAD" && transitNumber?.length !== 5)
      newErrors.transitNumber = "Please ensure your Transit Number has 5 digits";
    if (currency.value === "CAD" && !transitNumber)
      newErrors.transitNumber = "This is a required field";

    // Swift Code
    if (currency.value !== "AUD") {
      // for US clients, we don't need swift code if the Routing Number is provided
      if ((currency.value === "USD" && !abaRoutingNumber) || swiftCode) {
        if (swiftCode?.length < 8 || swiftCode?.length > 11)
          newErrors.swiftCode = "Please ensure your SWIFT Code has 8-11 digits";
        if (!swiftCode && !abaRoutingNumber)
          newErrors.swiftCode = "Either SWIFT Code or ABA Routing Number is required";
      } else if (currency.value !== "USD") {
        if (swiftCode?.length < 8 || swiftCode?.length > 11)
          newErrors.swiftCode = "Please ensure your SWIFT Code has 8-11 digits";
        if (!swiftCode) newErrors.swiftCode = "This is a required field";
      }
    }

    setFormErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      console.error("Bank Details Form had errors", newErrors);
    }

    return Object.keys(newErrors).length === 0;
  };

  return {
    formErrors,
    setFormErrors,
    isBeneficiaryAddressEntered,
    isBankAddressEntered,
    validateFields,
  };
};
